import React, { useState } from 'react';
import { PageHeader, useReveal, GlowDivider } from '../components/Shared';

const JOBS = [
  { id:1, title:'Postdoctoral Researcher — Computational Materials', dept:'Computational', type:'Full-time', location:'On-site', color:'teal',
    desc:'Lead DFT and ML-based property prediction for novel solid electrolytes. Work alongside our AI team to build next-generation machine-learned force fields.',
    reqs:['PhD in Materials Science, Chemistry, or Physics','Experience with DFT codes (VASP, Quantum ESPRESSO)','Python proficiency','Strong publication record'],
    deadline:'Open until filled' },
  { id:2, title:'Postdoctoral Researcher — AI & Materials Informatics', dept:'AI & Informatics', type:'Full-time', location:'On-site', color:'purple',
    desc:'Develop and apply graph neural networks and Bayesian optimization pipelines for autonomous materials discovery. Collaborate with robotics and experimental teams.',
    reqs:['PhD in CS, Data Science, or Materials Science','Deep learning frameworks (PyTorch / JAX)','Experience with graph neural networks','Materials background preferred'],
    deadline:'Open until filled' },
  { id:3, title:'Research Software Engineer', dept:'Software & Data', type:'Full-time', location:'Hybrid', color:'pink',
    desc:'Design and maintain the software infrastructure that powers our autonomous lab — from robotic control APIs to ML training pipelines and data dashboards.',
    reqs:['BS/MS in CS or Software Engineering','5+ years full-stack experience','Python, React, REST APIs','Experience with lab automation a plus'],
    deadline:'April 30, 2025' },
  { id:4, title:'PhD Student — Autonomous Synthesis (Fall 2025)', dept:'Graduate Program', type:'PhD Position', location:'On-site', color:'teal',
    desc:'Join our graduate program and focus on developing autonomous synthesis protocols for energy materials. Full funding and stipend provided.',
    reqs:['BS/MS in Chemistry, Materials Science, or ChemE','GRE not required','Research experience in synthesis','Strong motivation for interdisciplinary work'],
    deadline:'Rolling admission' },
  { id:5, title:'Lab Technician — Characterization & Testing', dept:'Operations', type:'Full-time', location:'On-site', color:'purple',
    desc:'Operate and maintain our characterization suite including XRD, SEM, impedance spectroscopy, and electrochemical cycling equipment.',
    reqs:['BS in Chemistry or Materials Science','Experience with characterization equipment','Detail-oriented and organized','Team player'],
    deadline:'March 31, 2025' },
];

const PERKS = [
  { icon:'💰', title:'Competitive Salary',    desc:'Market-leading compensation benchmarked against top research institutions and industry.' },
  { icon:'🏥', title:'Full Health Benefits',  desc:'Comprehensive medical, dental, and vision coverage for you and your dependents.' },
  { icon:'📚', title:'Learning Budget',        desc:'$3,000/year for conferences, courses, books, and professional development.' },
  { icon:'🌍', title:'Conference Travel',     desc:'Funding to present your research at top-tier international conferences.' },
  { icon:'⏰', title:'Flexible Hours',         desc:'Results-driven culture with flexible scheduling and generous PTO.' },
  { icon:'🤝', title:'Mentorship Program',    desc:'Structured mentorship from senior scientists and access to our industry network.' },
];

const colorMap  = { teal:'#00ffff', purple:'#a78bfa', pink:'#f9a8d4' };
const bgMap     = { teal:'rgba(0,255,255,0.07)', purple:'rgba(139,92,246,0.08)', pink:'rgba(244,114,182,0.07)' };
const borderMap = { teal:'rgba(0,255,255,0.16)', purple:'rgba(139,92,246,0.2)', pink:'rgba(244,114,182,0.16)' };

export default function Careers() {
  useReveal();
  const [open, setOpen] = useState(null);

  return (
    <>
      <PageHeader
        tag="Careers"
        title="Shape the Future of"
        accent="Materials Science"
        sub="Join a world-class team pushing the boundaries of what's possible at the intersection of AI, robotics, and materials discovery."
      />

      <GlowDivider />

      {/* Why join */}
      <section className="section">
        <div className="container">
          <div className="text-center mb-14 reveal">
            <span className="section-tag">Why D2S Lab</span>
            <h2 className="section-title">More Than a <span className="text-gradient">Job</span></h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-16">
            {PERKS.map((p,i) => (
              <div key={p.title} className="glass-card reveal p-8" style={{transitionDelay:`${i*0.08}s`}}>
                <div className="text-3xl mb-4">{p.icon}</div>
                <h3 className="font-display font-bold text-lg tracking-[-0.02em] mb-2">{p.title}</h3>
                <p className="text-sm font-light leading-relaxed" style={{color:'var(--text-2)'}}>{p.desc}</p>
              </div>
            ))}
          </div>

          {/* Culture quote */}
          <div className="glass-card reveal p-12 text-center" style={{position:'relative'}}>
            <div className="absolute top-0 left-0 right-0 h-[2px] rounded-t-[18px]"
              style={{background:'linear-gradient(90deg,transparent,#00ffff,#8b5cf6,transparent)'}} />
            <div className="font-display text-[4rem] leading-none mb-4" style={{color:'rgba(0,255,255,0.15)'}}>❝</div>
            <blockquote className="font-display font-semibold text-[1.35rem] tracking-[-0.03em] leading-relaxed mb-5 max-w-2xl mx-auto">
              We don't just do research — we build the infrastructure that makes the <span className="text-gradient">next decade</span> of materials science possible.
            </blockquote>
            <cite className="font-mono text-xs tracking-[3px] uppercase not-italic" style={{color:'var(--text-3)'}}>— Dr. Sarah Chen, Principal Investigator</cite>
          </div>
        </div>
      </section>

      <GlowDivider />

      {/* Open positions */}
      <section className="section section-alt">
        <div className="container">
          <div className="text-center mb-14 reveal">
            <span className="section-tag">Open Positions</span>
            <h2 className="section-title">Join Our <span className="text-gradient">Team</span></h2>
          </div>

          <div className="flex flex-col gap-4">
            {JOBS.map((job, i) => (
              <div key={job.id} className="glass-card reveal" style={{transitionDelay:`${i*0.08}s`,position:'relative'}}>
                <div className="absolute top-0 left-0 right-0 h-[2px] rounded-t-[18px]"
                  style={{background:`linear-gradient(90deg,transparent,${colorMap[job.color]},transparent)`}} />

                {/* Header row */}
                <div className="p-7 flex flex-wrap items-center justify-between gap-4 cursor-pointer"
                  onClick={() => setOpen(open===job.id ? null : job.id)}>
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{background:bgMap[job.color],border:`1px solid ${borderMap[job.color]}`}}>
                      <svg className="w-5 h-5" style={{color:colorMap[job.color]}} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-display font-bold text-base tracking-[-0.02em]">{job.title}</h3>
                      <div className="flex items-center gap-3 mt-1 flex-wrap">
                        <span className="font-mono text-[0.6rem] tracking-[2px] uppercase" style={{color:colorMap[job.color]}}>{job.dept}</span>
                        <span className="w-1 h-1 rounded-full" style={{background:'var(--text-3)'}} />
                        <span className="font-mono text-[0.6rem]" style={{color:'var(--text-3)'}}>{job.type}</span>
                        <span className="w-1 h-1 rounded-full" style={{background:'var(--text-3)'}} />
                        <span className="font-mono text-[0.6rem]" style={{color:'var(--text-3)'}}>{job.location}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="font-mono text-[0.62rem] tracking-wide hidden sm:block" style={{color:'var(--text-3)'}}>Deadline: {job.deadline}</span>
                    <svg className={`w-5 h-5 transition-transform duration-300 ${open===job.id?'rotate-180':''}`}
                      style={{color:colorMap[job.color]}} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M19 9l-7 7-7-7"/>
                    </svg>
                  </div>
                </div>

                {/* Expanded content */}
                <div className={`overflow-hidden transition-all duration-500 ${open===job.id ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'}`}>
                  <div className="px-7 pb-7 pt-0 border-t" style={{borderColor:'var(--border)'}}>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 pt-6">
                      <div>
                        <h4 className="font-display font-semibold text-sm mb-3 tracking-[-0.01em]">About the Role</h4>
                        <p className="text-sm font-light leading-relaxed mb-6" style={{color:'var(--text-2)'}}>{job.desc}</p>
                        <h4 className="font-display font-semibold text-sm mb-3 tracking-[-0.01em]">Requirements</h4>
                        <ul className="flex flex-col gap-2">
                          {job.reqs.map(r => (
                            <li key={r} className="flex items-center gap-3 text-sm font-light" style={{color:'var(--text-2)'}}>
                              <span className="w-[5px] h-[5px] rounded-full flex-shrink-0" style={{background:colorMap[job.color]}} />{r}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="flex flex-col justify-between">
                        <div className="p-6 rounded-xl mb-4" style={{background:'rgba(255,255,255,0.02)',border:'1px solid var(--border)'}}>
                          <p className="font-mono text-xs tracking-[2px] uppercase mb-2" style={{color:'var(--text-3)'}}>Application Deadline</p>
                          <p className="font-display font-bold text-lg" style={{color:colorMap[job.color]}}>{job.deadline}</p>
                        </div>
                        <a href="/contact" className="btn btn--primary w-full justify-center">
                          Apply Now
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <GlowDivider />

      {/* Spontaneous application */}
      <section className="section">
        <div className="container">
          <div className="glass-card reveal text-center p-14" style={{position:'relative'}}>
            <div className="absolute inset-0 rounded-[18px] pointer-events-none"
              style={{background:'radial-gradient(ellipse at center,rgba(0,255,255,0.03) 0%,transparent 70%)'}} />
            <span className="section-tag justify-center">Don't See Your Role?</span>
            <h2 className="section-title mb-3">Send a <span className="text-gradient">Spontaneous Application</span></h2>
            <p className="text-sm font-light leading-relaxed mx-auto mb-10 max-w-[480px]" style={{color:'var(--text-2)'}}>
              We're always interested in exceptional people. Send us your CV and a brief note about what you'd like to work on.
            </p>
            <a href="/contact" className="btn btn--primary">Send Your CV</a>
          </div>
        </div>
      </section>
    </>
  );
}
